/**
 * @file AdminMeetingDetailPage.tsx
 * @brief Super Admin → one MeetingIntel meeting, as a metadata card.
 *
 * @details
 * VERBLIZR staff never read meeting content (B2/W4, DES 22-10-2026). This page
 * shows what staff need to run the platform: title, owner, company, date,
 * duration and minutes, status and failure reason, counts, the bill and the
 * vendor cost. No transcript, intelligence, notes, audio, evidence, to-dos,
 * language picker, PDF or speaker naming. Actions: Retry / Regenerate /
 * Reprocess / Delete (the shared `MeetingRowActions` in `admin` scope).
 *
 * The schema strips every content key, so this holds even against a backend
 * that still sends content (BACKEND 1fd613a not deployed yet).
 *
 * @dependencies
 * - features/admin-meetings/{api, schemas}
 * - features/company-meetings/components/{MeetingRowActions, MeetingStatusBadge}
 * - components/feedback/StaffNoContent
 *
 * @references
 * - scratch/claude-code-report.md "## B2" (the contract)
 *
 * @author DES
 * @date 08-07-2026
 * @updated 22-10-2026 — metadata card, no content (W4)
 */

import type { ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, User } from "lucide-react";
import { readBackState } from "@/shared/navigation/backState";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/feedback/ErrorState";
import {
  PrivateToOwnerState,
  StaffNoContentNote,
} from "@/components/feedback/StaffNoContent";
import { isStaffNoContentError } from "@/shared/api/staffNoContent";
import {
  formatDateTime,
  formatDurationSeconds,
  formatFullName,
  formatMoneyMinor,
} from "@/shared/utils/formatters";
import { MeetingRowActions } from "@/features/company-meetings/components/MeetingRowActions";
import { MeetingStatusBadge } from "@/features/company-meetings/components/MeetingStatusBadge";
import { useAdminMeetingCogs, useAdminMeetingDetail } from "./api";
import {
  formatMeetingMinutes,
  meetingOwnerLabel,
  type AdminMeetingDetail,
} from "./schemas";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <GridCell>
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 text-[15px] font-semibold tabular-nums">{value}</div>
    </GridCell>
  );
}

const count = (n: number | null | undefined) => (n == null ? "—" : String(n));

function contentDetail(m: AdminMeetingDetail): string {
  return [
    m.transcript_segment_count != null ? `${m.transcript_segment_count} transcript lines` : null,
    m.has_recording ? "recording kept" : "no recording",
    m.has_mom_pdf ? "PDF made" : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function AdminMeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detail = useAdminMeetingDetail(id ?? null);
  const cogs = useAdminMeetingCogs(id ?? null);
  const meeting = detail.data;

  // Back to where the meeting was opened from (a to-do, Home, Inbox), else
  // the list (DES 13-10-2026).
  const back = readBackState(useLocation().state);
  const backToList = () => navigate(back?.backTo ?? "/admin/meetings");

  const backButton = (
    <button
      type="button"
      onClick={backToList}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {back?.backLabel ?? "Meeting Intel"}
    </button>
  );

  if (detail.isLoading) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand className="space-y-3">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </GridBand>
          <GridBand>
            <Skeleton className="h-40 w-full" />
          </GridBand>
        </GridBlock>
      </div>
    );
  }

  if (detail.error || !meeting) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand>{backButton}</GridBand>
          <GridBand>
            {isStaffNoContentError(detail.error) ? (
              <PrivateToOwnerState />
            ) : (
              <ErrorState
                title="Couldn't load this meeting"
                error={detail.error ?? "It may have been deleted."}
                onRetry={() => detail.refetch()}
              />
            )}
          </GridBand>
        </GridBlock>
      </div>
    );
  }

  const owner = formatFullName(meeting.owner_first_name, meeting.owner_last_name);
  const currency = meeting.currency ?? "GBP";
  const vendor = cogs.data;
  const vendorCost = cogs.isLoading
    ? "…"
    : !vendor?.measured
      ? "Not measured"
      : formatMoneyMinor(vendor.cogs?.cogs_total_cents ?? null, currency);

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="space-y-3">
          {backButton}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-xl font-semibold tracking-tight">
                {meeting.title?.trim() || "Untitled meeting"}
              </h2>
              <p className="mt-1 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  {owner !== "—" ? owner : meetingOwnerLabel(meeting)}
                  {meeting.owner_email && owner !== "—" ? ` · ${meeting.owner_email}` : null}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {meeting.company_name?.trim() || "Personal"}
                </span>
              </p>
            </div>
            <MeetingRowActions
              meeting={meeting}
              scope="admin"
              onDeleted={() => navigate("/admin/meetings")}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {meeting.status ? (
              <MeetingStatusBadge status={meeting.status} failureReason={meeting.failure_reason} />
            ) : null}
            {meeting.tier === "notes" ? (
              <span className="text-xs text-muted-foreground">Meeting Notes</span>
            ) : null}
          </div>
          {meeting.status === "error" && meeting.failure_reason ? (
            <p className="text-sm text-muted-foreground">
              Failure: {meeting.failure_reason}
              {meeting.failed_at ? ` · ${formatDateTime(meeting.failed_at)}` : null}
            </p>
          ) : null}
          {meeting.status === "resolved_pending" ? (
            <p className="text-sm text-muted-foreground">
              Waiting for speaker names. Only the owner or their company admin can
              name speakers; staff can reprocess or delete.
            </p>
          ) : null}
          <StaffNoContentNote />
        </GridBand>

        <GridCells className="grid-cols-2 md:grid-cols-4">
          <Fact label="Date" value={formatDateTime(meeting.started_at)} />
          <Fact
            label="Duration"
            value={formatDurationSeconds(Number(meeting.total_duration_seconds ?? 0))}
          />
          <Fact label="Minutes" value={formatMeetingMinutes(meeting)} />
          <Fact label="Source" value={meeting.source ?? "—"} />
          <Fact label="Participants" value={count(meeting.participant_count)} />
          <Fact label="Speakers" value={count(meeting.speaker_count)} />
          <Fact label="Decisions" value={count(meeting.decision_count)} />
          <Fact label="To-dos" value={count(meeting.todo_count)} />
          {/* `total_cost_cents` on a meeting = what the customer was billed;
              `charge_cents` = what Stripe captured. */}
          <Fact label="Billed" value={formatMoneyMinor(meeting.total_cost_cents, currency)} />
          <Fact label="Captured" value={formatMoneyMinor(meeting.charge_cents, currency)} />
          <Fact label="Vendor cost" value={vendorCost} />
          <Fact
            label="Margin"
            value={vendor?.margin_pct != null ? `${vendor.margin_pct}%` : "—"}
          />
        </GridCells>

        <GridBand>
          <PrivateToOwnerState detail={contentDetail(meeting)} withLine={false} />
        </GridBand>
      </GridBlock>
    </div>
  );
}
